import React from "react"
import { ColumnsType } from "antd/lib/table/Table"
import { GetCourseListWithPaginationPublicQuery, useSettingEndUserAppQuery } from "../../generated/graphql"
import {
    renderCoursePrice,
    renderCourseType,
    renderEntranceFee,
    renderFirstCourseLesson,
    renderFreePlaces,
    renderStatus,
    renderAgeRange,
    renderAdditionalShortInfo,
    renderRestrictions,
} from "../../views/TableColumnRenderers/TableColumnRenderers"
import BookingButton from "./BookingButton"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { useCurrency } from "../../helpers/currencyHelper"

export type CourseTableType = GetCourseListWithPaginationPublicQuery["coursesWithPaginationPublic"]["items"][number]

export const rowKey: (course: CourseTableType) => CourseTableType["id"] = (course) => course.id

export const useCourseTableColumns = ({
    columnsCount = 0,
    actionColumn,
}: {
    columnsCount?: number
    actionColumn?: Function
}): ColumnsType<CourseTableType> => {
    const mediaSize = useMediaSize()
    const currency = useCurrency()

    const maxColumnWidth = mediaSize.media === "xs" ? "100%" : columnsCount > 6 ? 100 : 200

    const { data: appSettingsData } = useSettingEndUserAppQuery()
    const memberDiscountText =
        appSettingsData?.settingEndUserAppPublic?.textEntries?.memberDiscountTitle ?? "Mitgliederpreis"

    const columns: ColumnsType<CourseTableType> = [
        {
            title: mediaSize.media === "xs" ? "Voraussetzungen" : "*",
            dataIndex: "restrictions",
            align: "center",
            key: "restrictions",
            render: (text, record: CourseTableType) => renderRestrictions({ record, media: mediaSize.media }),
        },
        {
            title: "Kursname",
            dataIndex: "courseType",
            key: "courseType",
            render: (text, record: CourseTableType) =>
                renderCourseType({
                    name: record.courseType.name,
                    color: record.courseType.color,
                    style: { minWidth: 100, whiteSpace: "nowrap" },
                }),
        },
        {
            title: "Kurzinfo",
            dataIndex: "additionalShortInfo",
            key: "additionalShortInfo",
            width: maxColumnWidth,
            render: (text, record) =>
                renderAdditionalShortInfo({
                    text: record.additionalShortInfo ?? undefined,
                    maxWidth: maxColumnWidth,
                }),
        },
        {
            title: "Stadt",
            dataIndex: "city",
            key: "city",
            responsive: columnsCount > 6 ? ["lg"] : undefined,
            render: (text, record) => record.location.city,
        },

        {
            title: "Kursnummer",
            dataIndex: "prefixedCourseNumber",
            key: "prefixedCourseNumber",
            responsive: columnsCount > 8 ? ["lg"] : undefined,
            render: (_, record) => record.prefixedCourseNumber,
        },
        {
            title: "Veranstaltungsort",
            dataIndex: "location",
            key: "location",
            render: (text, record: CourseTableType) => (
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.3 }}>{record.location.name}</div>
            ),
        },
        {
            title: "Kursbeginn",
            dataIndex: "lessons",
            key: "startDateTime",
            render: (text, record: CourseTableType) =>
                renderFirstCourseLesson(record.startDateTime, record.endDateTimeFirstCourseLesson),
        },
        {
            title: "Alter",
            dataIndex: "age",
            key: "age",
            render: (text, record: CourseTableType) => (
                <div>
                    <span style={{ lineHeight: 1.3, whiteSpace: "nowrap" }}>
                        {renderAgeRange(record.courseType.minAge, record.courseType.maxAge)}
                    </span>
                </div>
            ),
        },
        {
            title: "Freie Plätze",
            dataIndex: "freePlaces",
            key: "freePlaces",
            align: "center",
            render: (text, record) => renderFreePlaces(record.freePlaces, record.maxAttendees),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            align: "center",
            render: (text, record) => renderStatus(record.freePlaces, record.maxAttendees, true),
        },
        {
            title: "Kursgebühr",
            dataIndex: "coursePrice",
            key: "coursePrice",
            align: "right",
            render: (_, record) => renderCoursePrice(record, memberDiscountText, mediaSize, currency),
        },
        {
            title: "Anz. Einheiten",
            align: "center",
            dataIndex: "lessonCount",
            key: "lessonCount",
            responsive: columnsCount > 4 ? ["md"] : undefined,
            render: (text, record) => record.lessonCount,
        },
        {
            title: "Badeintritt / Einheit",
            dataIndex: "includedEntranceFee",
            key: "includedEntranceFee",
            responsive: columnsCount > 4 ? ["md"] : undefined,
            render: (text, record) => renderEntranceFee(record.coursePrice?.[0].includedEntranceFee, mediaSize, currency),
        },
        {
            title: "",
            dataIndex: "actionColumn",
            key: "actionColumn",
            fixed: "right",
            render: (text, record) => renderActions(record, actionColumn),
        },
    ]

    return columns
}

// ACTION BUTTONS---------------------------------------------------------------------------

const renderActions = (record: CourseTableType, actionColumn?: Function): JSX.Element => {
    return <BookingButton record={record} block />
}
